import React, { useCallback, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, Alert, Pressable, ScrollView, Modal, FlatList, Share, Platform } from 'react-native';
import { useLocalSearchParams, useRouter, useFocusEffect } from 'expo-router';
import { useTranslation } from 'react-i18next';
import '../../i18n';
import { useAuth } from '../../features/auth/useAuth';
import { useDb } from '../../db/client';
import { getProject, listProjectSupplies, updateProjectPricingOverrides, type ProjectSupplyView } from '../../features/projects/projectRepository';
import { ensureProfileRow } from '../../db/writes';
import { listClosedTimeEntries } from '../../features/session/timeEntries';
import { calculatePrice, type PriceBreakdown } from '../../domain/pricing';
import { formatDurationHms } from '../../domain/timer';
import { formatLocaleNumber } from '../shared/formatLocaleNumber';
import { parseLocaleNumber } from '../shared/parseLocaleNumber';
import type { Project, Profile, TimeEntry } from '../../db/schema';
import { colors, fonts, radii, spacing } from '../theme';
import { TextField } from '../shared/TextField';
import { Button } from '../shared/Button';

/** Formata horas fracionárias como "4h30" (spec S04, ver exemplo da tela de
 * preço) — puramente para EXIBIÇÃO da parcela de mão de obra; a soma em si
 * (`breakdown.laborHours`) já vem pronta e com precisão total de
 * `domain/pricing.ts`, nunca recalculada aqui. */
function formatHoursLabel(hours: number): string {
  const totalMinutes = Math.round(Math.max(0, hours) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return m > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
}

/** Mesmo padrão de formatDate "sujo" já usado em ProjectsListScreen.tsx —
 * pequeno demais para justificar extrair para um helper compartilhado. */
function formatDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
}

function currency(value: number): string {
  return `R$ ${formatLocaleNumber(value)}`;
}

/** Resultado de tentar interpretar o texto de um campo de OVERRIDE
 * (nullable): campo vazio é um valor válido (`null` = "usa o perfil"),
 * diferente de `parseLocaleNumber` puro (que trata vazio como zero — certo
 * para os campos NOT NULL extraCosts/saleFeePct, errado aqui). */
function parseOverrideText(text: string): { value: number | null; valid: boolean } {
  const trimmed = text.trim();
  if (trimmed === '') return { value: null, valid: true };
  const parsed = parseLocaleNumber(text);
  if (parsed === null) return { value: null, valid: false };
  return { value: parsed, valid: true };
}

/** Monta o texto simples de compartilhamento (spec S04: "sem PDF no MVP",
 * WhatsApp é o alvo) a partir do MESMO `PriceBreakdown` já calculado — nunca
 * recalcula nada, só formata. Mesma visibilidade condicional de linhas da
 * tela (custos extras/taxa de venda só aparecem se > 0). */
function buildShareText(
  projectName: string,
  breakdown: PriceBreakdown,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const lines: string[] = [projectName, ''];
  lines.push(`${t('pricing.materialsLabel')}: ${currency(breakdown.materialsCost)}`);
  lines.push(`${t('pricing.laborLabel')}: ${currency(breakdown.laborCost)}`);
  if (breakdown.extraCosts > 0) {
    lines.push(`${t('pricing.extraCostsLabel')}: ${currency(breakdown.extraCosts)}`);
  }
  lines.push(`${t('pricing.totalCostLabel')}: ${currency(breakdown.totalCost)}`);
  lines.push(`${t('pricing.markupLabel', { pct: formatLocaleNumber(breakdown.markupPct) })}: + ${currency(breakdown.markupAmount)}`);
  if (breakdown.saleFeePct > 0) {
    lines.push(`${t('pricing.basePriceLabel')}: ${currency(breakdown.basePrice)}`);
    lines.push(`${t('pricing.saleFeeLabel', { pct: formatLocaleNumber(breakdown.saleFeePct) })}: ${currency(breakdown.finalPrice)}`);
  }
  lines.push('');
  lines.push(`${t('pricing.finalPriceLabel')}: ${currency(breakdown.finalPrice)}`);
  return lines.join('\n');
}

/**
 * Tela de Preço (Etapa 13 — spec S04): o "coração do produto". Regras
 * invioláveis que este componente PRECISA respeitar:
 *  - Nenhum cálculo de preço acontece aqui — tudo passa por
 *    `calculatePrice` (domain/pricing.ts, já testado desde a Etapa 7). Esta
 *    tela só formata para exibição (`formatLocaleNumber`) e monta o texto de
 *    compartilhamento a partir do MESMO `PriceBreakdown`.
 *  - Overrides/campos avançados são gravados via `updateProjectPricingOverrides`
 *    (src/features/projects/projectRepository.ts), que passa por
 *    `updateRecord` (SQLite + outbox na mesma transação) — nunca grava
 *    direto no Supabase.
 *  - `useFocusEffect` (não `useEffect` simples) recarrega projeto/insumos/
 *    sessões a cada vez que esta tela ganha foco — critério de aceite "pausar
 *    o cronômetro → preço reflete as novas horas imediatamente ao abrir a
 *    tela" (mesmo padrão de ProjectsListScreen.tsx).
 */
export function PricingScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { projectId: rawProjectId } = useLocalSearchParams<{ projectId: string }>();
  const projectId = Array.isArray(rawProjectId) ? rawProjectId[0] : rawProjectId;
  const { user } = useAuth();
  const { db, ready } = useDb();

  const [project, setProject] = useState<Project | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [projectSupplies, setProjectSupplies] = useState<ProjectSupplyView[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [hourlyRateOverrideText, setHourlyRateOverrideText] = useState('');
  const [markupPctOverrideText, setMarkupPctOverrideText] = useState('');
  const [extraCostsText, setExtraCostsText] = useState('0');
  const [saleFeePctText, setSaleFeePctText] = useState('0');
  const [savingOverrides, setSavingOverrides] = useState(false);

  const [materialsModalVisible, setMaterialsModalVisible] = useState(false);
  const [laborModalVisible, setLaborModalVisible] = useState(false);

  const load = useCallback(async () => {
    if (!ready || !db || !user || !projectId) return;
    setLoading(true);
    try {
      const [loadedProject, loadedProfile] = await Promise.all([getProject(db, projectId), ensureProfileRow(db, user.id)]);
      if (!loadedProject) {
        setNotFound(true);
        return;
      }

      const [supplies, entries] = await Promise.all([listProjectSupplies(db, projectId), listClosedTimeEntries(db, projectId)]);

      setProject(loadedProject);
      setProfile(loadedProfile);
      setProjectSupplies(supplies);
      setTimeEntries(entries);
      setHourlyRateOverrideText(loadedProject.hourlyRateOverride != null ? String(loadedProject.hourlyRateOverride) : '');
      setMarkupPctOverrideText(loadedProject.markupPctOverride != null ? String(loadedProject.markupPctOverride) : '');
      setExtraCostsText(String(loadedProject.extraCosts));
      setSaleFeePctText(String(loadedProject.saleFeePct));
    } catch (error) {
      console.error('PricingScreen: falha ao carregar preço', error);
      Alert.alert(t('pricing.errorTitle'), t('pricing.genericErrorMessage'));
    } finally {
      setLoading(false);
    }
  }, [ready, db, user, projectId, t]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load])
  );

  const breakdown: PriceBreakdown | null = useMemo(() => {
    if (!project || !profile) return null;
    return calculatePrice({
      project,
      profile,
      projectSupplies,
      timeEntries,
    });
  }, [project, profile, projectSupplies, timeEntries]);

  const hourlyRateUndefined = project != null && profile != null && profile.hourlyRate === 0 && project.hourlyRateOverride == null;

  const handleSaveOverrides = async () => {
    if (!db || !project) return;

    const hourlyRateOverride = parseOverrideText(hourlyRateOverrideText);
    const markupPctOverride = parseOverrideText(markupPctOverrideText);
    const extraCosts = parseLocaleNumber(extraCostsText);
    const saleFeePct = parseLocaleNumber(saleFeePctText);

    if (!hourlyRateOverride.valid || !markupPctOverride.valid || extraCosts === null || saleFeePct === null) {
      Alert.alert(t('pricing.saveErrorTitle'), t('pricing.invalidNumberMessage'));
      return;
    }

    setSavingOverrides(true);
    try {
      const updated = await updateProjectPricingOverrides(db, project.id, {
        hourlyRateOverride: hourlyRateOverride.value,
        markupPctOverride: markupPctOverride.value,
        extraCosts,
        saleFeePct,
      });
      setProject(updated);
      Alert.alert(t('pricing.title'), t('pricing.savedOverrides'));
    } catch (error) {
      console.error('PricingScreen: falha ao salvar overrides de preço', error);
      Alert.alert(t('pricing.saveErrorTitle'), (error as Error).message);
    } finally {
      setSavingOverrides(false);
    }
  };

  const handleShare = async () => {
    if (!project || !breakdown) return;
    const message = buildShareText(project.name, breakdown, t);
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && !navigator.share && navigator.clipboard) {
        await navigator.clipboard.writeText(message);
        Alert.alert(t('pricing.title'), t('pricing.copiedToClipboard') || 'Resumo copiado para a área de transferência!');
        return;
      }
      await Share.share({ message });
    } catch (error) {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(message);
          Alert.alert(t('pricing.title'), t('pricing.copiedToClipboard') || 'Resumo copiado para a área de transferência!');
          return;
        } catch (clipboardError) {
          console.error('PricingScreen: falha ao copiar para a área de transferência', clipboardError);
        }
      }
      console.error('PricingScreen: falha ao compartilhar preço', error);
      Alert.alert(t('pricing.shareUnavailableTitle'), (error as Error).message);
    }
  };

  if (!ready || loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator color={colors.sageDeep} />
        <Text style={styles.muted}>{t('pricing.loading')}</Text>
      </View>
    );
  }

  if (notFound || !project || !breakdown) {
    return (
      <View style={styles.centered}>
        <Text style={styles.muted}>{t('pricing.notFound')}</Text>
        <Button title={t('pricing.back')} onPress={() => router.back()} />
      </View>
    );
  }

  const showExtraCostsRow = breakdown.extraCosts > 0;
  const showSaleFeeRow = breakdown.saleFeePct > 0;

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <View style={styles.headerRow}>
        <Button title={`< ${t('pricing.back')}`} variant="ghost" onPress={() => router.back()} />
        <Text style={styles.title}>{t('pricing.title')}</Text>
      </View>

      <View style={styles.breakdown}>
        <Pressable style={styles.row} onPress={() => setMaterialsModalVisible(true)}>
          <Text style={styles.rowLabel}>{t('pricing.materialsLabel')}</Text>
          <Text style={styles.rowValue}>{currency(breakdown.materialsCost)}</Text>
        </Pressable>

        <Pressable style={styles.row} onPress={() => setLaborModalVisible(true)}>
          <View>
            <Text style={styles.rowLabel}>{t('pricing.laborLabel')}</Text>
            {hourlyRateUndefined ? (
              <Pressable onPress={() => router.push('/perfil')}>
                <Text style={styles.warningText}>{t('pricing.laborUndefinedWarning')}</Text>
              </Pressable>
            ) : (
              <Text style={styles.rowSubtitle}>
                {t('pricing.laborSubtitle', { hours: formatHoursLabel(breakdown.laborHours), rate: currency(breakdown.hourlyRate) })}
              </Text>
            )}
          </View>
          <Text style={styles.rowValue}>{currency(breakdown.laborCost)}</Text>
        </Pressable>

        {showExtraCostsRow && (
          <View style={styles.row}>
            <Text style={styles.rowLabel}>{t('pricing.extraCostsLabel')}</Text>
            <Text style={styles.rowValue}>{currency(breakdown.extraCosts)}</Text>
          </View>
        )}

        <View style={styles.divider} />

        <View style={styles.row}>
          <Text style={styles.rowLabelBold}>{t('pricing.totalCostLabel')}</Text>
          <Text style={styles.rowValueBold}>{currency(breakdown.totalCost)}</Text>
        </View>

        <View style={styles.row}>
          <Text style={styles.rowLabel}>{t('pricing.markupLabel', { pct: formatLocaleNumber(breakdown.markupPct) })}</Text>
          <Text style={styles.rowValue}>+ {currency(breakdown.markupAmount)}</Text>
        </View>

        <View style={styles.divider} />

        {showSaleFeeRow ? (
          <>
            <View style={styles.row}>
              <Text style={styles.rowLabel}>{t('pricing.basePriceLabel')}</Text>
              <Text style={styles.rowValue}>{currency(breakdown.basePrice)}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.rowLabelBold}>{t('pricing.saleFeeLabel', { pct: formatLocaleNumber(breakdown.saleFeePct) })}</Text>
              <Text style={styles.finalPrice}>{currency(breakdown.finalPrice)}</Text>
            </View>
          </>
        ) : (
          <View style={styles.row}>
            <Text style={styles.rowLabelBold}>{t('pricing.finalPriceLabel')}</Text>
            <Text style={styles.finalPrice}>{currency(breakdown.finalPrice)}</Text>
          </View>
        )}
      </View>

      {project.practicedPrice != null && (
        <View style={styles.practicedPriceBox}>
          <Text style={styles.rowLabel}>{t('pricing.practicedPriceLabel')}</Text>
          <Text style={styles.rowValue}>{currency(project.practicedPrice)}</Text>
        </View>
      )}

      <View style={styles.shareButtonWrapper}>
        <Button title={t('pricing.shareButton')} onPress={handleShare} />
      </View>

      <Pressable style={styles.advancedToggle} onPress={() => setShowAdvanced((v) => !v)}>
        <Text style={styles.advancedToggleText}>{t(showAdvanced ? 'pricing.moreOptionsHide' : 'pricing.moreOptionsShow')}</Text>
      </Pressable>

      {showAdvanced && (
        <View style={styles.advancedSection}>
          <Text style={styles.label}>{t('pricing.hourlyRateOverrideLabel')}</Text>
          <TextField
            placeholder={t('pricing.hourlyRateOverridePlaceholder')}
            value={hourlyRateOverrideText}
            onChangeText={setHourlyRateOverrideText}
            keyboardType="decimal-pad"
            editable={!savingOverrides}
          />

          <Text style={styles.label}>{t('pricing.markupOverrideLabel')}</Text>
          <TextField
            placeholder={t('pricing.markupOverridePlaceholder')}
            value={markupPctOverrideText}
            onChangeText={setMarkupPctOverrideText}
            keyboardType="decimal-pad"
            editable={!savingOverrides}
          />

          <Text style={styles.label}>{t('pricing.extraCostsInputLabel')}</Text>
          <TextField
            value={extraCostsText}
            onChangeText={setExtraCostsText}
            keyboardType="decimal-pad"
            editable={!savingOverrides}
          />

          <Text style={styles.label}>{t('pricing.saleFeeInputLabel')}</Text>
          <TextField
            value={saleFeePctText}
            onChangeText={setSaleFeePctText}
            keyboardType="decimal-pad"
            editable={!savingOverrides}
          />

          {savingOverrides ? (
            <ActivityIndicator color={colors.sageDeep} />
          ) : (
            <Button title={t('pricing.saveOverridesButton')} onPress={handleSaveOverrides} />
          )}
        </View>
      )}

      <Modal visible={materialsModalVisible} animationType="slide" transparent onRequestClose={() => setMaterialsModalVisible(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{t('pricing.materialsModalTitle')}</Text>
            {projectSupplies.length === 0 ? (
              <Text style={styles.muted}>{t('pricing.materialsModalEmpty')}</Text>
            ) : (
              <FlatList
                data={projectSupplies}
                keyExtractor={(item) => item.id}
                renderItem={({ item }) => (
                  <View style={styles.modalRow}>
                    <Text style={styles.modalRowLabel}>
                      {item.supplyName} · {formatLocaleNumber(item.quantity)} {item.supplyUnit}
                    </Text>
                    <Text style={styles.modalRowValue}>{currency(item.unitPriceSnapshot)}</Text>
                  </View>
                )}
              />
            )}
            <Button title={t('pricing.closeModalButton')} variant="ghost" onPress={() => setMaterialsModalVisible(false)} />
          </View>
        </View>
      </Modal>

      <Modal visible={laborModalVisible} animationType="slide" transparent onRequestClose={() => setLaborModalVisible(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{t('pricing.laborModalTitle')}</Text>
            {timeEntries.length === 0 ? (
              <Text style={styles.muted}>{t('pricing.laborModalEmpty')}</Text>
            ) : (
              <FlatList
                data={timeEntries}
                keyExtractor={(item) => item.id}
                renderItem={({ item }) => {
                  const durationSeconds = item.endedAt ? (item.endedAt.getTime() - item.startedAt.getTime()) / 1000 : 0;
                  return (
                    <View style={styles.modalRow}>
                      <Text style={styles.modalRowLabel}>{formatDate(item.startedAt)}</Text>
                      <Text style={styles.modalRowValue}>{formatDurationHms(durationSeconds)}</Text>
                    </View>
                  );
                }}
              />
            )}
            <Button title={t('pricing.closeModalButton')} variant="ghost" onPress={() => setLaborModalVisible(false)} />
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { padding: spacing.md, backgroundColor: colors.sand },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.md, backgroundColor: colors.sand },
  muted: { fontFamily: fonts.body, color: colors.thread, marginTop: spacing.sm },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontFamily: fonts.display, fontSize: 20, color: colors.ink },
  breakdown: { marginTop: spacing.md },
  row: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 10 },
  rowLabel: { fontFamily: fonts.body, fontSize: 15, color: colors.inkSoft },
  rowLabelBold: { fontFamily: fonts.bodyBold, fontSize: 15, color: colors.ink },
  rowSubtitle: { fontFamily: fonts.body, fontSize: 12, color: colors.thread, marginTop: 2 },
  rowValue: { fontFamily: fonts.body, fontSize: 15, color: colors.inkSoft },
  rowValueBold: { fontFamily: fonts.bodyBold, fontSize: 15, color: colors.ink },
  finalPrice: { fontFamily: fonts.bodyBold, fontSize: 18, color: colors.sageDeep },
  warningText: { fontFamily: fonts.body, fontSize: 12, color: colors.clay, marginTop: 2, textDecorationLine: 'underline' },
  divider: { height: 1, backgroundColor: colors.line, marginVertical: spacing.sm },
  practicedPriceBox: {
    marginTop: spacing.md,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: colors.line,
  },
  shareButtonWrapper: { marginTop: spacing.lg },
  advancedToggle: { marginTop: spacing.xl },
  advancedToggleText: { fontFamily: fonts.bodyMedium, color: colors.sageDeep },
  advancedSection: { marginTop: spacing.md },
  label: { fontFamily: fonts.bodyMedium, fontSize: 13, color: colors.inkSoft, marginTop: spacing.md, marginBottom: spacing.xs },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(44,47,34,0.4)', justifyContent: 'flex-end' },
  modalContent: {
    backgroundColor: colors.sand,
    borderTopLeftRadius: radii.md,
    borderTopRightRadius: radii.md,
    padding: spacing.md,
    maxHeight: '70%',
  },
  modalTitle: { fontFamily: fonts.display, fontSize: 17, color: colors.ink, marginBottom: spacing.md },
  modalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.line,
  },
  modalRowLabel: { fontFamily: fonts.body, fontSize: 14, color: colors.inkSoft, flexShrink: 1, paddingRight: spacing.sm },
  modalRowValue: { fontFamily: fonts.body, fontSize: 14, color: colors.inkSoft },
});
